import React from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Stack,
  Chip,
  SelectChangeEvent,
} from '@mui/material';
import { DateRange, Today, CalendarMonth } from '@mui/icons-material';

interface DateRangeSelectorProps {
  fromDate: string;
  toDate: string;
  onFromDateChange: (date: string) => void;
  onToDateChange: (date: string) => void;
  onApply?: () => void;
  showPresets?: boolean;
  showQuickSelect?: boolean;
  financialYearStart?: number; // Month number (4 for April)
}

type PresetType = 'today' | 'thisWeek' | 'thisMonth' | 'lastMonth' | 'thisQuarter' | 'lastQuarter' | 'thisFY' | 'lastFY' | 'custom';

const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
  fromDate,
  toDate,
  onFromDateChange,
  onToDateChange,
  onApply,
  showPresets = true,
  showQuickSelect = true,
  financialYearStart = 4, // April
}) => {
  const [preset, setPreset] = React.useState<PresetType>('custom');

  // Get current financial year
  const getCurrentFY = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1;

    if (month >= financialYearStart) {
      return { start: year, end: year + 1 };
    }
    return { start: year - 1, end: year };
  };

  // Get previous financial year
  const getPreviousFY = () => {
    const currentFY = getCurrentFY();
    return { start: currentFY.start - 1, end: currentFY.end - 1 };
  };

  // Get current quarter dates (Indian FY quarters)
  const getCurrentQuarter = () => {
    const today = new Date();
    const month = today.getMonth() + 1;
    const year = today.getFullYear();
    const fy = getCurrentFY();

    if (month >= 4 && month <= 6) {
      return { from: `${fy.start}-04-01`, to: `${fy.start}-06-30` };
    } else if (month >= 7 && month <= 9) {
      return { from: `${fy.start}-07-01`, to: `${fy.start}-09-30` };
    } else if (month >= 10 && month <= 12) {
      return { from: `${fy.start}-10-01`, to: `${fy.start}-12-31` };
    } else {
      return { from: `${fy.end}-01-01`, to: `${fy.end}-03-31` };
    }
  };

  // Get previous quarter dates
  const getPreviousQuarter = () => {
    const today = new Date();
    const month = today.getMonth() + 1;
    const fy = getCurrentFY();
    const prevFY = getPreviousFY();

    if (month >= 4 && month <= 6) {
      return { from: `${prevFY.end}-01-01`, to: `${prevFY.end}-03-31` };
    } else if (month >= 7 && month <= 9) {
      return { from: `${fy.start}-04-01`, to: `${fy.start}-06-30` };
    } else if (month >= 10 && month <= 12) {
      return { from: `${fy.start}-07-01`, to: `${fy.start}-09-30` };
    } else {
      return { from: `${fy.start}-10-01`, to: `${fy.start}-12-31` };
    }
  };

  const formatDateForInput = (date: Date) => {
    return date.toISOString().split('T')[0];
  };

  const applyPreset = (presetType: PresetType) => {
    setPreset(presetType);
    const today = new Date();
    let from: string, to: string;

    switch (presetType) {
      case 'today':
        from = to = formatDateForInput(today);
        break;

      case 'thisWeek':
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        from = formatDateForInput(startOfWeek);
        to = formatDateForInput(endOfWeek);
        break;

      case 'thisMonth':
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        from = formatDateForInput(startOfMonth);
        to = formatDateForInput(endOfMonth);
        break;

      case 'lastMonth':
        const startOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
        from = formatDateForInput(startOfLastMonth);
        to = formatDateForInput(endOfLastMonth);
        break;

      case 'thisQuarter':
        const currentQ = getCurrentQuarter();
        from = currentQ.from;
        to = currentQ.to;
        break;

      case 'lastQuarter':
        const prevQ = getPreviousQuarter();
        from = prevQ.from;
        to = prevQ.to;
        break;

      case 'thisFY':
        const fy = getCurrentFY();
        from = `${fy.start}-04-01`;
        to = `${fy.end}-03-31`;
        break;

      case 'lastFY':
        const prevFY = getPreviousFY();
        from = `${prevFY.start}-04-01`;
        to = `${prevFY.end}-03-31`;
        break;

      default:
        return;
    }

    onFromDateChange(from);
    onToDateChange(to);
  };

  const handlePresetChange = (event: SelectChangeEvent) => {
    applyPreset(event.target.value as PresetType);
  };

  const quickPresets = [
    { label: 'Today', value: 'today', icon: <Today fontSize="small" /> },
    { label: 'This Month', value: 'thisMonth', icon: <CalendarMonth fontSize="small" /> },
    { label: 'This FY', value: 'thisFY', icon: <DateRange fontSize="small" /> },
  ];

  return (
    <Box>
      {showQuickSelect && (
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          {quickPresets.map((qp) => (
            <Chip
              key={qp.value}
              icon={qp.icon}
              label={qp.label}
              onClick={() => applyPreset(qp.value as PresetType)}
              color={preset === qp.value ? 'primary' : 'default'}
              variant={preset === qp.value ? 'filled' : 'outlined'}
              size="small"
            />
          ))}
        </Stack>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-end">
        {showPresets && (
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Period</InputLabel>
            <Select
              value={preset}
              label="Period"
              onChange={handlePresetChange}
            >
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="thisWeek">This Week</MenuItem>
              <MenuItem value="thisMonth">This Month</MenuItem>
              <MenuItem value="lastMonth">Last Month</MenuItem>
              <MenuItem value="thisQuarter">This Quarter</MenuItem>
              <MenuItem value="lastQuarter">Last Quarter</MenuItem>
              <MenuItem value="thisFY">This FY</MenuItem>
              <MenuItem value="lastFY">Last FY</MenuItem>
              <MenuItem value="custom">Custom</MenuItem>
            </Select>
          </FormControl>
        )}

        <TextField
          label="From Date"
          type="date"
          size="small"
          value={fromDate}
          onChange={(e) => {
            setPreset('custom');
            onFromDateChange(e.target.value);
          }}
          slotProps={{
            inputLabel: { shrink: true },
          }}
        />

        <TextField
          label="To Date"
          type="date"
          size="small"
          value={toDate}
          onChange={(e) => {
            setPreset('custom');
            onToDateChange(e.target.value);
          }}
          slotProps={{
            inputLabel: { shrink: true },
          }}
        />

        {onApply && (
          <Button
            variant="contained"
            onClick={onApply}
            startIcon={<DateRange />}
            size="medium"
          >
            Apply
          </Button>
        )}
      </Stack>
    </Box>
  );
};

export default DateRangeSelector;
